export function getProduct(blueprint, handle, options) {
    return new Promise((resolve) => {
        let storefrontUrl = blueprint.custom['v-storefront'];
        let accessKey = blueprint.custom['v-storefront-token'];

        if (options?.storefrontUrl) {
            storefrontUrl = options.storefrontUrl;
        }

        if (options?.storefrontAccessToken) {
            accessKey = options?.storefrontAccessToken;
        }

        const query = {
            query: `
                query getProductByHandle($id: String!) {
                    product(handle: $id) {
                        title
                        priceRange {
                            minVariantPrice {
                                amount
                                currencyCode
                            }
                            maxVariantPrice {
                                amount
                                currencyCode
                            }
                        }
                        variants(first:100) {
                            edges {
                                node {
                                    id
                                    quantityAvailable
                                    sku
                                    price {
                                        amount
                                        currencyCode
                                    }
                                }
                            }
                        }
                    }
                }
                `,
            variables: {
                id: `${handle}`,
            },
            operationName: 'getProductByHandle',
        };

        fetch(storefrontUrl, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': accessKey,
            },
            body: JSON.stringify(query),
        })
            .then((response) => response.json())
            .then((data) => {
                const variants = [];

                data?.data?.product?.variants?.edges?.forEach((variant) => {
                    if (variant.node?.price?.amount) {
                        const sku = variant.node.sku;
                        const ats = parseFloat(variant.node.quantityAvailable);
                        const price = parseFloat(variant.node.price.amount);
                        const currency = variant.node.price.currencyCode;

                        variants.push({
                            variant: variant.node.id,
                            sku,
                            ats,
                            price,
                            currency,
                        });
                    }
                });

                resolve(variants);
            });
    });
}

function formatEndpoint(url) {
    if (!url.endsWith('/api/2023-01/graphql.json')) {
        if (url.endsWith('/')) {
            return `${url}api/2023-01/graphql.json`;
        }

        return `${url}/api/2023-01/graphql.json`;
    }

    return url;
}

export function getVariantById(blueprint, variantId, options) {
    return new Promise((resolve) => {
        let storefrontUrl = blueprint.custom['v-storefront'];
        let accessKey = blueprint.custom['v-storefront-token'];

        if (options?.storefrontUrl) {
            storefrontUrl = options.storefrontUrl;
        }

        if (options?.storefrontDomain) {
            storefrontUrl = `https://${options.storefrontDomain}`;
        }

        if (options?.storefrontAccessToken) {
            accessKey = options?.storefrontAccessToken;
        }

        storefrontUrl = formatEndpoint(storefrontUrl);

        // Modified query to fetch variant directly by ID
        const query = {
            query: `
                {
                    productVariant(id: "gid://shopify/ProductVariant/${variantId}") {
                        id
                        quantityAvailable
                        sku
                        price {
                            amount
                            currencyCode
                        }
                    }
                }
            `,
        };

        fetch(storefrontUrl, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': accessKey,
            },
            body: JSON.stringify(query),
        })
            .then((response) => response.json())
            .then((data) => {
                const variants = [];

                const variant = data?.data?.productVariant;

                if (variant && variant.price?.amount) {
                    const sku = variant.sku;
                    const ats = parseFloat(variant.quantityAvailable);
                    const price = parseFloat(variant.price.amount);
                    const currency = variant.price.currencyCode;

                    variants.push({
                        variant: variant.id.replace('gid://shopify/ProductVariant/', ''),
                        sku,
                        ats,
                        price,
                        currency,
                    });
                }

                resolve(variants);
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error('Error fetching variant:', error);

                resolve([]);
            });
    });
}

export function getVariantsByIds(blueprint, variantIds, options) {
    return new Promise((resolve) => {
        let storefrontUrl = blueprint.custom['v-storefront'];
        let accessKey = blueprint.custom['v-storefront-token'];
        if (options?.storefrontUrl) {
            storefrontUrl = options.storefrontUrl;
        }
        if (options?.storefrontDomain) {
            storefrontUrl = `https://${options.storefrontDomain}`;
        }
        if (options?.storefrontAccessToken) {
            accessKey = options?.storefrontAccessToken;
        }
        storefrontUrl = formatEndpoint(storefrontUrl);

        // Split variantIds into chunks of 250
        const chunkSize = 250;
        const variantIdChunks = [];
        for (let i = 0; i < variantIds.length; i += chunkSize) {
            variantIdChunks.push(variantIds.slice(i, i + chunkSize));
        }

        // Process each chunk with a separate API call
        const fetchPromises = variantIdChunks.map((chunk) => {
            // Create an array of IDs in the gid format required by Shopify
            const formattedIds = chunk.map((id) => `"gid://shopify/ProductVariant/${id}"`).join(',');

            // Enhanced query to fetch inventory tracking information
            const query = {
                query: `
                    {
                        nodes(ids: [${formattedIds}]) {
                            ... on ProductVariant {
                                id
                                quantityAvailable
                                sku
                                price {
                                    amount
                                    currencyCode
                                }
                                availableForSale
                                product {
                                    id
                                    title
                                }
                            }
                        }
                    }
                `,
            };

            return fetch(storefrontUrl, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': accessKey,
                },
                body: JSON.stringify(query),
            })
            .then((response) => response.json())
            .then((data) => {
                const chunkVariants = [];
                const nodes = data?.data?.nodes || [];
                nodes.forEach((variant) => {
                    if (variant && variant.price?.amount) {
                        const sku = variant.sku;
                        let ats = parseFloat(variant.quantityAvailable || 0);
                        if (ats <= 0 && variant.availableForSale) {
                            ats = 9999;
                        }
                        const price = parseFloat(variant.price.amount);
                        const currency = variant.price.currencyCode;
                        chunkVariants.push({
                            variant: variant.id.replace('gid://shopify/ProductVariant/', ''),
                            sku,
                            ats,
                            price,
                            currency,
                        });
                    }
                });
                return chunkVariants;
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error('Error fetching variants chunk:', error);
                return []; // Return empty array for this chunk if there's an error
            });
        });

        // Wait for all fetch promises to resolve and combine the results
        Promise.all(fetchPromises)
            .then((chunkResults) => {
                // Flatten the array of arrays into a single array of variants
                const allVariants = chunkResults.flat();
                resolve(allVariants);
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error('Error processing variant batches:', error);
                resolve([]);
            });
    });
}
